
import { Call } from '@twilio/voice-sdk';
import { getDevice, initializeDevice } from './deviceManager';
import { logTwilioError } from '../debug';

let currentCall: Call | null = null;

/**
 * Make a call using Twilio Voice SDK
 */
export const makeCall = async (to: string) => {
  try {
    let device = getDevice();
    
    if (!device) {
      const initResult = await initializeDevice();
      if (!initResult.success) {
        return {
          success: false,
          error: initResult.error
        };
      }
      device = initResult.device;
    }

    if (!device?.state || device.state !== 'registered') {
      return {
        success: false,
        error: 'Voice device not ready'
      };
    }

    console.log('📞 Making voice call to:', to);

    // Make the call
    const call = await device.connect({
      params: {
        To: to
      }
    });

    currentCall = call;

    // Set up comprehensive call event listeners
    call.on('accept', () => {
      console.log('✅ Call accepted');
    });

    call.on('disconnect', (disconnectCall) => {
      console.log('📴 Call disconnected');
      console.log('📴 Disconnect call object:', disconnectCall);
      
      // Ensure currentCall is cleared
      if (currentCall === call) {
        currentCall = null;
      }
    });

    call.on('cancel', () => {
      console.log('❌ Call cancelled');
      if (currentCall === call) {
        currentCall = null;
      }
    });

    call.on('reject', () => {
      console.log('❌ Call rejected');
      if (currentCall === call) {
        currentCall = null;
      }
    });

    call.on('error', (error) => {
      console.error('❌ Call error:', error);
      logTwilioError(error, 'voiceCall');
      
      // Clear current call on error
      if (currentCall === call) {
        currentCall = null;
      }
    });

    return {
      success: true,
      callSid: call.parameters.CallSid,
      call
    };
  } catch (error: any) {
    console.error('❌ Error making voice call:', error);
    logTwilioError(error, 'makeVoiceCall');
    return {
      success: false,
      error: error.message || 'Failed to make call'
    };
  }
};

/**
 * End the current call
 */
export const endCall = async () => {
  try {
    if (currentCall) {
      console.log('📞 Ending call with SID:', currentCall.parameters.CallSid);
      currentCall.disconnect();
      currentCall = null;
      return { success: true };
    }
    return {
      success: false,
      error: 'No active call to end'
    };
  } catch (error: any) {
    console.error('❌ Error ending call:', error);
    // Always clear the current call even if disconnect fails
    currentCall = null;
    return {
      success: false,
      error: error.message || 'Failed to end call'
    };
  }
};

/**
 * Mute/unmute the current call
 */
export const muteCall = async (mute: boolean) => {
  try {
    if (currentCall) {
      currentCall.mute(mute);
      return { success: true };
    }
    return {
      success: false,
      error: 'No active call to mute'
    };
  } catch (error: any) {
    console.error('❌ Error muting call:', error);
    return {
      success: false,
      error: error.message || 'Failed to mute call'
    };
  }
};

/**
 * Get current call status
 */
export const getCurrentCall = () => {
  return currentCall;
};

/**
 * Force clear current call (for cleanup scenarios)
 */
export const clearCurrentCall = () => {
  currentCall = null;
};

/**
 * Handle incoming calls
 */
export const setupIncomingCallHandler = () => {
  const device = getDevice();
  if (device) {
    device.on('incoming', (call) => {
      console.log('📞 Incoming call:', call.parameters.From);
      currentCall = call;
    });
  }
};
